import Container from 'react-bootstrap/Container';
import { Formik } from 'formik';
import { Card, Col, Form, Row } from 'react-bootstrap';
import Btn from '../../components/Btn/Btn';
import { useApiClient } from '../../hooks/useApiClient';
import { useEffect, useMemo, useState } from 'react';
import VehiclePdfTemplate from '../../components/VehicleDetails/VehiclePdfTemplate';
import dayjs from 'dayjs';
import { useAuth } from '../../hooks/useAuth';
import ValidationError from '../../components/ValidationError/ValidationError';
import MotornetVehicleSelector, { MotornetSelectedVehicle, getMakeName } from '../../motornet/common/MotornetVehicleSelector';
import { productionFetchParams } from '../../motornet/common/selectorMeta';
import type { QuotationPdfTag } from 'src/quotations/types';
import RequiredFormLabel from '../../components/RequiredFormLabel/RequiredFormLabel';
import OptionalsCard from '../../components/OptionalsCard/OptionalsCard';
import { mapServices } from '../../utils/pdfUtils';
import RestrictedRoute from '../../components/RestrictedRoute/RestrictedRoute';
import SpinnerButton from '../../components/SpinnerButton/SpinnerButton';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { DealerDTO, RoleEnum } from '../../api';
import { Storage } from 'aws-amplify';
import { dealersMap } from 'src/utils/users';
import usePdfStore from '../../store/PdfStore';
import { capitalize, randomString } from 'src/utils';
import { isMarket } from 'src/utils/markets';
import { Markets } from '../../types';

const VehicleOfferPage = () => {
	const { t } = useTranslation();
	const { user, userRole } = useAuth();
	const [isBusy, setBusy] = useState<boolean>(false);
	const { apiClient, handleApiResponse } = useApiClient();
	const [dealerLogo, setDealerLogo] = useState<string | null>(null);
	const [symbolIncendioFurto, setSymbolIncendioFurto] = useState('%');
	const { setPdfDetail } = usePdfStore();

	const [selectedVehicle, setSelectedVehicle] = useState<MotornetSelectedVehicle>({
		brand: null,
		model: null,
		version: null,
	});
	const [vehicleImages, setVehicleImages] = useState<string[]>([]);
	const [vehicleSpecs, setVehicleSpecs] = useState<any>(null);

	const initialValues = useMemo(() => {
		return {
			data_valid: dayjs().format('YYYY-MM-DD'),
			name: '',
			durata: '',
			anticipo: '',
			km_totali: '',
			importo_privato: '',
			importo_azienda: '',
			offerType: 'company',
			servizi: {
				treno_di_gomme: 'No',
				kasko: '',
				rca: '',
				auto_sostitutiva: 'No',
				i_f: '',
				manutenzione_ordinaria: true,
				manutenzione_straordinaria: true,
			},
			optionals: [
				{
					codice_costruttore: '',
					descrizione: '',
					valore: 0,
				},
			],
		};
	}, []);

	const getUserDealer = async () => {
		if (
			[RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL].includes(
				user.attributes['custom:role']
			)
		) {
			if (user.attributes['custom:reference_partner']) {
				const userDealers = JSON.parse(
					user.attributes['custom:reference_partner']
				);

				if (userDealers.length) {
					const userDealer = userDealers[0].toUpperCase();
					const dealers: DealerDTO[] = await handleApiResponse(
						apiClient.dealerController.getRegisterDealers()
					);

					for (let d of dealers) {
						let ragioneSociale = d.ragione_sociale
							.toUpperCase()
							.replaceAll('_', ' ');

						if (
							ragioneSociale === userDealer ||
							(dealersMap[d.ragione_sociale] !== undefined &&
								dealersMap[d.ragione_sociale] === userDealer)
						) {
							return d;
						}
					}
				}
			}
		}

		return null;
	};

	const load = async () => {
		if (
			![RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL].includes(
				userRole
			)
		) {
			return;
		}

		if (!user.attributes['custom:reference_partner']) {
			return;
		}

		const userDealers = JSON.parse(user.attributes['custom:reference_partner']);
		if (!userDealers.length) {
			return;
		}

		const dealer = await getUserDealer();
		if (dealer && dealer.logo?.length) {
			const logo = await Storage.get(dealer.logo, {
				bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_DEALER_LOGO,
				cacheControl: 'no-cache, no-store, must-revalidate',
			});

			setDealerLogo(logo);
		}
	};

	useEffect(() => {
		load();
	}, []);

	const modelLabel = (model: any) => model?.data?.gammaModello?.descrizione || model?.label || '';

	// On version selection, fetch the full Motornet vehicle once and denormalize the
	// specs + image URLs used by the generated PDF.
	const fetchVehicleDetails = async (version: any) => {
		const eurotax = version?.data?.codiceEurotax;
		if (!eurotax) {
			setVehicleImages([]);
			setVehicleSpecs(null);
			return;
		}
		setBusy(true);
		try {
			const vehicleKind = (selectedVehicle.brand as any)?.data?.vehicleKind ?? 'auto';
			const payload = await apiClient.motornetControllerService.getMotornetVehicle(eurotax, vehicleKind, productionFetchParams(version?.data, selectedVehicle.model?.data));
			const m: any = payload?.details?.modello;
			setVehicleSpecs({
				alimentazione: m?.alimentazione?.descrizione ?? null,
				cambio: m?.cambio?.descrizione ?? m?.nomeCambio ?? null,
				cavalli: m?.cavalliTotale ?? m?.hp ?? null,
				cilindrata: m?.cilindrata ?? null,
				kw: m?.kw ?? m?.potenzaTotale ?? null,
				porte: m?.porte ?? null,
				posti: m?.posti ?? null,
			});
			setVehicleImages((payload?.images ?? []).map((img: any) => img?.url).filter(Boolean));
		} catch (e) {
			console.warn('Failed to fetch motornet vehicle details', e);
			setVehicleSpecs(null);
			setVehicleImages([]);
		} finally {
			setBusy(false);
		}
	};

	const validateFn = (values) => {
		const errors: any = {};
		const REQUIRED_MESSAGE = 'Obbligatorio';

		if (!values.name) {
			errors.name = REQUIRED_MESSAGE;
		}

		if (!values.durata) {
			errors.durata = REQUIRED_MESSAGE;
		}

		if (!values.km_totali) {
			errors.km_totali = REQUIRED_MESSAGE;
		}

		return errors;
	};

	const getPdfData = async (offerta: typeof initialValues) => {
		setBusy(true);

		offerta = _.cloneDeep(offerta);
		// Add € or % to I/F
		if (offerta.servizi.i_f.length) {
			offerta.servizi.i_f = offerta.servizi.i_f + ' ' + symbolIncendioFurto;
		}

		try {
			// Motornet images are direct URLs; embed the first one (or a placeholder).
			const imageList: any = vehicleImages.length
				? [{ url: vehicleImages[0] }]
				: [{ url: '/images/placeholder.png' }];

			const brand: any = selectedVehicle.brand;
			const model: any = selectedVehicle.model;
			const version: any = selectedVehicle.version;
			const quotation: QuotationPdfTag = {
				source: 'custom_offer',
				vehicle: {
					makeCode: brand?.value ? String(brand.value) : null,
					makeLabel: getMakeName(brand),
					modelCode: model?.value ? String(model.value) : null,
					modelLabel: modelLabel(model),
					trimLabel: version?.label ?? null,
					vehicleCode: version?.data?.codiceEurotax ?? null,
					vehicleKind: brand?.data?.vehicleKind ?? null,
				},
			};

			const pdfData = {
				lista_offerte: [
					{
						id: 'customPdf',
						durata: offerta.durata,
						km_totali: offerta.km_totali,
						servizi: offerta.servizi,
						anticipo: offerta.anticipo,
						importo_azienda: offerta.importo_azienda,
						importo_privato: offerta.importo_privato,
					},
				],
				pdf: {
					data_validita: offerta.data_valid,
					name: offerta.name,
					userName: user.attributes.given_name,
					userSurname: user.attributes.family_name,
					userMail: user.attributes.email,
					offerType: offerta.offerType,
					offerId: 'customPdf',
					dealerDetail: { logo: dealerLogo },
					services: mapServices('', offerta.servizi),
				},
				dati_veicolo: {
					marca: getMakeName(selectedVehicle.brand),
					modello: modelLabel(selectedVehicle.model),
					allestimento: selectedVehicle.version?.label,
					alimentazione: vehicleSpecs?.alimentazione,
					cambio: vehicleSpecs?.cambio,
					cavalli: vehicleSpecs?.cavalli,
					cilindrata: vehicleSpecs?.cilindrata,
					kw: vehicleSpecs?.kw,
					porte: vehicleSpecs?.porte,
					posti: vehicleSpecs?.posti,
				},
				optionals: offerta.optionals.filter((el) => el.descrizione.length > 0),
				image_list: imageList,
				quotation,
			};
			return pdfData;
		} finally {
			setBusy(false);
		}
	};

	const onSubmitFn = async (
		offerta: typeof initialValues,
		{ setSubmitting }
	) => {
		getPdfData(offerta).then((pdfData) => {
			const idempotency = randomString(10);
			setPdfDetail({ data: { ...pdfData, idempotency } });
		});
	};

	const hasSelectedVehicle = useMemo(() => {
		return Boolean(selectedVehicle?.brand && selectedVehicle?.model && selectedVehicle?.version);
	}, [selectedVehicle])

	return (
		<RestrictedRoute
			to={[
				RoleEnum.BACKOFFICE,
				RoleEnum.MASTER,
				RoleEnum.ADMIN,
				RoleEnum.SALES,
				RoleEnum.SALES_DEALER,
				RoleEnum.SALES_INTERNAL,
				RoleEnum.AREA_MANAGER,
			]}
		>
			<Container fluid>
				<div className="display-6 my-4">{t('create_offer')}</div>

				<Formik
					initialValues={{ ...initialValues }}
					validate={validateFn}
					onSubmit={onSubmitFn}
				>
					{({
						values,
						errors,
						touched,
						handleChange,
						handleBlur,
						handleSubmit,
						isSubmitting,
					}) => (
						<Form onSubmit={handleSubmit}>
							<Card className="mt-2">
								<Card.Body>
									<Row>
										<Col md={6}>
											<Form.Group className="mb-3">
												<Form.Label>
													{t('quotation_recipient')}
													<span className="red-ast">*</span>
												</Form.Label>
												<Form.Control
													onChange={handleChange}
													onBlur={handleBlur}
													name="name"
													value={values?.name || ''}
												/>
												<ValidationError
													{...{ errors, touched, name: 'name' }}
												/>
												<Row className="mt-2">
													<Col md={4}>
														<Form.Check
															onChange={handleChange}
															onBlur={handleBlur}
															value={'company'}
															checked={values.offerType === 'company'}
															name="offerType"
															type="radio"
															label={t('company')}
															className="hor-check-box-big"
														/>
													</Col>
													<Col md={4}>
														<Form.Check
															onChange={handleChange}
															onBlur={handleBlur}
															value={'private'}
															checked={values.offerType === 'private'}
															name="offerType"
															type="radio"
															label={t('individual')}
															className="hor-check-box-big"
														/>
													</Col>
												</Row>
											</Form.Group>
										</Col>
										{dealerLogo && (
											<Col
												md={6}
												className="d-flex justify-content-center align-items-center"
											>
												<img
													src={dealerLogo}
													alt=""
													height={100}
													style={{ maxWidth: '100%' }}
												/>
											</Col>
										)}
									</Row>

									<div className="py-3">
										<hr />
									</div>

									{/* SELECT BRAND and MODEL DROPDOWN */}
									<RequiredFormLabel label={t('select_vehicle')} />
									<Row>
										<MotornetVehicleSelector
											selectedVehicle={selectedVehicle}
											onBrandChange={(brand) => {
												setSelectedVehicle({ brand, model: null, version: null });
												setVehicleImages([]);
												setVehicleSpecs(null);
											}}
											onModelChange={(model) => {
												setSelectedVehicle((prev) => ({ ...prev, model, version: null }));
												setVehicleImages([]);
												setVehicleSpecs(null);
											}}
											onVersionChange={(version) => {
												setSelectedVehicle((prev) => ({ ...prev, version }));
												fetchVehicleDetails(version);
											}}
										/>
									</Row>

									<div className="py-3">
										<hr />
									</div>
									<Row>
										<Col md={6}>
											<Form.Group className="mb-3">
												<Form.Label>
													{t('duration')} ({t('months')})
													<span className="red-ast">*</span>
												</Form.Label>
												<Form.Control
													required
													onChange={handleChange}
													onBlur={handleBlur}
													name="durata"
													pattern="[0-9]*"
													value={values?.durata || ''}
												/>
												<ValidationError
													{...{ errors, touched, name: 'durata' }}
												/>
											</Form.Group>
										</Col>

										<Col md={6}>
											<Form.Group className="mb-3">
												<Form.Label>
													{t('km/year')}
													<span className="red-ast">*</span>
												</Form.Label>
												<Form.Control
													onChange={handleChange}
													onBlur={handleBlur}
													required
													value={values?.km_totali || ''}
													name="km_totali"
													type="number"
												/>
												<ValidationError
													{...{ errors, touched, name: 'km_totali' }}
												/>
											</Form.Group>
										</Col>

										<Col md={6}>
											<Form.Group className="mb-3">
												<Form.Label>
													{t('downpayment')} + {t('vat')}
												</Form.Label>
												<Form.Control
													onChange={handleChange}
													onBlur={handleBlur}
													value={values?.anticipo}
													name="anticipo"
													type="number"
												/>
											</Form.Group>
										</Col>

										<Col md={6}>
											<Form.Group className="mb-3">
												<Form.Label> {t('private_rental_fees')}</Form.Label>
												<Form.Control
													onChange={handleChange}
													onBlur={handleBlur}
													value={values?.importo_privato || ''}
													name="importo_privato"
													type="number"
												/>
												<ValidationError
													{...{ errors, touched, name: 'importo_privato' }}
												/>
											</Form.Group>
										</Col>

										<Col md={6}>
											<Form.Group className="mb-3">
												{/* Canone P.IVA */}
												<Form.Label>{t('business_rental_fee')}</Form.Label>
												<Form.Control
													onChange={handleChange}
													onBlur={handleBlur}
													value={values?.importo_azienda || ''}
													name="importo_azienda"
													type="number"
												/>
												<ValidationError
													{...{ errors, touched, name: 'importo_azienda' }}
												/>
											</Form.Group>
										</Col>
										<Col md={6}>
											<Form.Group className="mb-3">
												<Form.Label>{t('validity_date')}</Form.Label>
												<Form.Control
													onChange={handleChange}
													onBlur={handleBlur}
													value={values?.data_valid || ''}
													name="data_valid"
													type="date"
												/>
												<ValidationError
													{...{ errors, touched, name: 'data_valid' }}
												/>
											</Form.Group>
										</Col>

										<div className="py-3">
											<hr />
										</div>

										<Col md={6}>
											<Form.Group className="mb-3">
												<Form.Label>{t('set_of_tyres')}</Form.Label>
												<Form.Control
													onChange={handleChange}
													onBlur={handleBlur}
													value={values?.servizi?.treno_di_gomme || ''}
													name="servizi.treno_di_gomme"
													type="text"
													className={`${
														errors.servizi?.treno_di_gomme &&
														touched.servizi?.treno_di_gomme
															? 'error-border'
															: ''
													}`}
												/>
											</Form.Group>
										</Col>

										{isMarket(Markets.it) ? (
											<>
												<Col md={5} className="pe-1">
													<Form.Group className="mb-3">
														<Form.Label>{t('i/f')}</Form.Label>
														<Form.Control
															onChange={handleChange}
															onBlur={handleBlur}
															value={values?.servizi?.i_f}
															name="servizi.i_f"
															type="text"
															pattern="[0-9,',']*"
														/>
													</Form.Group>
												</Col>

												<Col md={1} className="ps-0">
													<Form.Group className="mt-2">
														<Form.Label />
														<Form.Select
															name="sym_if"
															value={symbolIncendioFurto}
															onChange={(e) =>
																setSymbolIncendioFurto(e.target.value)
															}
														>
															<option key="%" value="%">
																%
															</option>
															<option key="€" value="€">
																€
															</option>
														</Form.Select>
													</Form.Group>
												</Col>
											</>
										) : (
											<></>
										)}

										<Col md={6}>
											<Form.Group className="mb-3">
												<Form.Label>{t('replacement_car')}</Form.Label>
												<Form.Control
													onChange={handleChange}
													onBlur={handleBlur}
													value={values?.servizi?.auto_sostitutiva || ''}
													name="servizi.auto_sostitutiva"
													type="text"
													className={`${
														errors.servizi?.auto_sostitutiva &&
														touched.servizi?.auto_sostitutiva
															? 'error-border'
															: ''
													}`}
												/>
											</Form.Group>
										</Col>

										{isMarket(Markets.it) ? (
											<Col md={5}>
												<Form.Group className="mb-3">
													<Form.Label>
														{isMarket('it') ? t('rca') : capitalize(t('rca'))}{' '}
														(€)
													</Form.Label>
													<Form.Control
														onChange={handleChange}
														onBlur={handleBlur}
														value={values?.servizi?.rca}
														name="servizi.rca"
														pattern="[0-9,',']*"
														type="text"
														className={`${
															errors.servizi?.rca && touched.servizi?.rca
																? 'error-border'
																: ''
														}`}
													/>
												</Form.Group>
											</Col>
										) : (
											<></>
										)}
										{/* <Row className="d-flex align-items-center"> */}

										<Col md={6}>
											<Form.Group className="mb-3">
												<Form.Label>{t('kasko')} (€)</Form.Label>
												<Form.Control
													onChange={handleChange}
													onBlur={handleBlur}
													value={values?.servizi?.kasko}
													name="servizi.kasko"
													// pattern="[0-9,',']*"
													type={isMarket(Markets.it) ? 'number' : 'text'}
													className={`${
														errors.servizi?.kasko && touched.servizi?.kasko
															? 'error-border'
															: ''
													}`}
												/>
											</Form.Group>
										</Col>

										<Col md={6} className="d-flex align-items-center">
											<Col md={6}>
												<Form.Group className="d-flex align-items-center">
													<Form.Check
														onChange={handleChange}
														onBlur={handleBlur}
														checked={values?.servizi?.manutenzione_ordinaria}
														name="servizi.manutenzione_ordinaria"
														type="checkbox"
														style={{ transform: 'scale(1.3)' }}
														className={`me-2 ${
															errors.servizi?.manutenzione_ordinaria &&
															touched.servizi?.manutenzione_ordinaria
																? 'error-border'
																: ''
														}`}
														id="ordinaryMaintenanceCheckbox"
													/>
													<Form.Label
														htmlFor="ordinaryMaintenanceCheckbox"
														style={{ margin: '0px' }}
													>
														{t('ordinary_maintenance')}
													</Form.Label>
												</Form.Group>
											</Col>

											<Col md={6}>
												<Form.Group className="d-flex align-items-center">
													<Form.Check
														onChange={handleChange}
														onBlur={handleBlur}
														checked={
															values?.servizi?.manutenzione_straordinaria
														}
														name="servizi.manutenzione_straordinaria"
														type="checkbox"
														style={{ transform: 'scale(1.3)' }}
														className={`me-2 ${
															errors.servizi?.manutenzione_straordinaria &&
															touched.servizi?.manutenzione_straordinaria
																? 'error-border'
																: ''
														}`}
														id="straordinaryMaintenanceCheckbox"
													/>
													<Form.Label
														htmlFor="straordinaryMaintenanceCheckbox"
														style={{ margin: '0px' }}
													>
														{t('straordinary_maintenance')}
													</Form.Label>
												</Form.Group>
											</Col>
										</Col>
									</Row>
								</Card.Body>
							</Card>

							<div className="display-7 my-4">{t('optionals')}</div>

							<OptionalsCard
								values={values}
								handleChange={handleChange}
								hideCode={true}
								addOptionDisabled={false}
								onOptionAdded={() => {}}
							/>

							<Btn
								type="submit"
								icon="arrow-right"
								className="mt-4 float-end"
								disabled={isSubmitting || isBusy || !hasSelectedVehicle}
							>
								<SpinnerButton
									label={i18next.t('download_quotation')}
									busy={isSubmitting || isBusy}
								/>
							</Btn>
						</Form>
					)}
				</Formik>

				<VehiclePdfTemplate />
			</Container>
		</RestrictedRoute>
	);
};

export default VehicleOfferPage;
